
<template>
  <div class="design-toggle-container">
    <label class="design-label">Design Type *</label>
    <v-btn-toggle
      v-model="selectedDesign"
      mandatory
      class="design-toggle"
      @update:model-value="handleDesignChange"
    >
      <v-btn
        value="classic"
        class="toggle-btn classic-btn"
        :class="{ active: selectedDesign === 'classic' }"
      >
        <div class="btn-content">
          <div class="btn-text">Classic Design</div>
          <div class="btn-image">
            <img :src="classicPlate" alt="Classic Design" class="design-image" />
          </div>
        </div>
      </v-btn>
      <v-btn
        value="new"
        class="toggle-btn new-btn"
        :class="{ active: selectedDesign === 'new' }"
      >
        <div class="btn-content">
          <div class="btn-text">New Design</div>
          <div class="btn-image">
            <img :src="newPlate" alt="New Design" class="design-image" />
          </div>
        </div>
      </v-btn>
    </v-btn-toggle>
    <div v-if="!selectedDesign" class="error-text">Design type is required</div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
// Import images using Vite's asset handling
import classicPlate from '@/assets/classic-plate-design.png';
import newPlate from '@/assets/new-plate-design.png';

const emit = defineEmits(['update:modelValue']);
const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  }
});

const selectedDesign = ref(props.modelValue || null);

const handleDesignChange = (value) => {
  emit('update:modelValue', value);
};

// Watch for external changes
watch(() => props.modelValue, (newValue) => {
  selectedDesign.value = newValue;
}, { immediate: true });
</script>


<style scoped>
.design-toggle-container {
  width: 100%;
  margin: 24px 0 32px 0;
  padding-bottom: 16px;
  position: relative;
  z-index: 1;
}

.design-label {
  display: block;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 12px;
  font-weight: 500;
}

.design-toggle {
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: visible; /* <-- allow content to overflow if needed */
  margin-bottom: 16px;
  min-height: 120px; /* <-- ensure enough space for images */
}

.toggle-btn {
  flex: 1;
  min-height: 120px !important; /* <-- more space for image */
  background: white !important;
  border: none !important;
  border-radius: 0 !important;
  padding: 16px 12px !important;
  transition: all 0.3s ease !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.toggle-btn:not(:last-child) {
  border-right: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.toggle-btn.active {
  background: #f5f5f5 !important;
  border-color: #b30000 !important;
}

.toggle-btn:hover {
  background: #fafafa !important;
}

.btn-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 12px;
}

.btn-text {
  font-size: 16px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  text-transform: none !important;
  line-height: 1.4;
}

.btn-image {
  width: 120px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9f9f9;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  margin-top: 4px;
}

.design-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

.error-text {
  color: #ff5252;
  font-size: 12px;
  margin-top: 8px;
}

/* Active state styling */
.toggle-btn.active .btn-text {
  color: #b30000;
  font-weight: 700;
}

.toggle-btn.active .btn-image {
  border-color: #b30000;
  background: white;
}

/* Remove Vuetify button group styling */
:deep(.v-btn-group) {
  box-shadow: none !important;
  border: none !important;
}

:deep(.v-btn--variant-elevated) {
  box-shadow: none !important;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .design-toggle-container {
    margin: 20px 0 28px 0;
  }
  .toggle-btn {
    min-height: 100px !important;
    padding: 12px 8px !important;
  }
  .btn-image {
    width: 100px;
    height: 45px;
  }
  .btn-text {
    font-size: 14px;
  }
}
</style>